import { createComponentStore } from '../../utils/createComponentStore';

const keyAuto = () =>
  'create_record_' + (Math.floor(Math.random() * 90000000) + 10000).toString();

const initData = () => ({
  outputKey: keyAuto(),
  columns: [],
  targetDatabaseId: null,
});

const onLoadData = (rawData, component) => {
  const data = { ...initData(), ...rawData };
  // v1 compat: key → outputKey
  if (rawData.key && !rawData.outputKey) {
    data.outputKey = rawData.key;
    delete data.key;
  }
  // v1 compat: columnValue → value within columns
  if (Array.isArray(data.columns)) {
    data.columns = data.columns.map((col) => {
      if (col.columnValue !== undefined && col.value === undefined) {
        const { columnValue, ...rest } = col;
        return { ...rest, value: columnValue };
      }
      // v2 stores value as "value", inline UI reads "columnValue"
      if (col.value !== undefined && col.columnValue === undefined) {
        return { ...col, columnValue: col.value };
      }
      return col;
    });
  }
  // v1 compat: targetDatabaseId is stored at componentData top-level (sibling
  // of `data`), not inside `data`. Pull it in so both load (display) and save
  // (round-trip back to componentData.targetDatabaseId via the buildComponentSavePayload
  // spread) preserve it. Without this, opening the inline panel and saving any
  // edit would silently wipe the database selection on the next Mongo $set of
  // components.$.componentData.
  // Execution reads the top-level target when both shapes exist. A stale
  // nested value must not select another table and replace its column bindings
  // when the inline editor loads.
  if (component?.componentData?.targetDatabaseId) {
    data.targetDatabaseId = component.componentData.targetDatabaseId;
  }
  return data;
};

const { createStore, getStore, defaultStore } = createComponentStore({
  type: 'create_database_record',
  endpoint: 'create_database_record',
  registryKey: 'create_database_record',
  initData,
  onLoadData,

  extraState: () => ({
    placeholders: [],
    position: null,
    databaseList: [],
  }),

  extraActions: (set, get) => ({
    // Backward-compat alias used by the legacy full template page. Kept as a
    // belt-and-suspenders second hydration path; setComponentData now also
    // pulls targetDatabaseId from the v1 sibling location via onLoadData.
    setCreateDatabaseRecordTemplate: (component) => {
      get().setComponentData(component);
    },

    setKey: (value) =>
      set((s) => ({ data: { ...s.data, outputKey: value } })),

    setData: (columns) =>
      set((s) => ({ data: { ...s.data, columns } })),

    clearData: () =>
      set((s) => ({ data: { ...s.data, columns: [] } })),

    setDatabaseList: (databaseList) => set({ databaseList }),

    setTargetDatabaseId: (targetDatabaseId) =>
      set((s) => ({ data: { ...s.data, targetDatabaseId } })),

    setPlaceholders: (placeholders) => set({ placeholders: [...placeholders] }),

    setFocus: (index) => set({ position: index, placeholders: [] }),

    keyAuto,

    getTemplate: () => {
      const s = get();
      return {
        componentId: s.componentId,
        name: s.name,
        data: JSON.parse(JSON.stringify(s.data)),
        type: s.type,
        lastModified: s.lastModified,
        placeholders: [...s.placeholders],
        position: s.position,
        databaseList: s.databaseList,
      };
    },
  }),
});

export { createStore as createCreateDatabaseRecordStore };
export const getCreateDatabaseRecordStore = getStore;
export default defaultStore;
